import { autorun, makeAutoObservable, runInAction } from 'mobx';
import { codes } from 'currency-codes';
import { config } from 'react-transition-group';
import { SocketConnectionStatuses, WSClient } from 'BackEnd/WebSocket';
import {
  TICKER_CATEGORY,
  TICKERS_CATEGORIES,
} from 'common/constants/tickers.constants';
import { log } from 'common/utils/logging.util';
import {
  getAccountSettingsFromLS,
  LocalStorage,
} from 'common/bridges/local-storage.bridge';
import {
  ACCOUNT_NAMES,
  ACCOUNT_TYPES,
} from 'common/constants/account.constants';
import { appConfig } from 'config';
import { BonusAPI } from 'BackEnd/API';
import {
  BackendError,
  captureError,
  InfrastructureError,
  SocketError,
} from 'common/utils/error.util';
import {
  trackAnalyticEvent,
  trackPageEvent,
} from 'common/utils/client-analytics.util';
import { closeOrder, OrderTypes } from 'common/constants/trading.constants';
import { Notifier } from 'common/services/notification.service';
import i18n from 'i18n';
import { getStreamUrl } from 'common/utils/socket.utils';
import { sortByTimeField } from 'pages/AccountOperation/util';
import { first, isErrorMessage, last } from 'common/utils/common.utils';
import { PricingService } from 'common/services/pricing.service';
import { syncObservableArray } from 'common/utils/mobx.util';
import { CRYPTO_TICKERS } from 'common/constants/cryptocurrency-ticker.constants';
import { DEFAULT_CURRENCY_ICONS } from 'themes/defaultTheme/icons-rwtrade.constants';
import { DISONA_CURRENCY_ICONS } from 'themes/disonaTheme/icons-disona.constants';
import { IS_DISONA, IS_ROCKWEST } from 'common/utils/style.util';
import { getIntervalByValue, prepareOrder } from '../../utils/chart.utils';
import AuthStore from '../auth.store';
import { AccountService } from '../../services/account.service';
import {
  MILLISECONDS_PER_SECOND,
  SECONDS_PER_MINUTE,
} from '../../constants/time.constants';
import {
  PRICING_STREAM_URL,
  PRICING_TYPES,
  TRADING_STREAM_URL,
  TRADING_TYPES,
} from '../../constants/socket.constants';
import { RawNewDealStore } from '../../../pages/Trading/module';
import { CANDLE_COUNT, CHART_TYPES } from '../../constants/chart.constants';
import { QuotesService } from './quotesService';
import { TradingViewService } from './tradingViewService';

export class _ExnessAccount {
  _account;

  openOrders = [];

  pendingOrders = [];

  selectedSymbolName;

  timeFrame;

  chartType = CHART_TYPES.CANDLES;

  depositList = [];

  withdrawList = [];

  isOpeningOrder = false;

  instruments = [];

  symbolsSubscribers = {};

  lastTick = {};

  isStreamInitiated = false;

  isPricingSocketInitiated = false;

  tradingSocketStatus = SocketConnectionStatuses.Connecting;

  selectedCategory = TICKER_CATEGORY.TOP;

  candlesResponse = [];

  quotesResponse = [];

  activeTabView = 'new';

  isOpenTrade = false;

  chartRef = null;

  shiftTime = 0;

  serverTime = 0;

  requestedTime = 0;

  boundaryCandleRequested = false;

  operations = [];

  precision = {};

  loading = false;

  accountBonus = null;

  newDealStore = new RawNewDealStore();

  signalStore = new RawNewDealStore();

  quotesService = new QuotesService(this, '.ds');

  tradingViewService = new TradingViewService();

  lastHourCandles = {};

  constructor(account, callbackFromParent, { isLazy = false } = {}) {
    makeAutoObservable(this);
    this._account = account;
    const accountSettingsLS = getAccountSettingsFromLS(this.accountNumber);
    if (!window.accs) window.accs = {};
    window.accs[account.account_id] = this;
    this.timeFrame = accountSettingsLS.timeFrame;
    this.chartType = accountSettingsLS.chartType;
    this.instruments = accountSettingsLS.instruments;

    autorun(() => {
      log(
        `${this.accountNumber} ${
          this.isTradingSocketConnected ? '🔌' : '🚫'
        } trading socket`
      );
    });

    this.instruments.forEach((value) => {
      this.precision[value.symbol] = value.digits;
    });

    if (!isLazy && !account.archived) {
      this._getPSPList(callbackFromParent);
    }

    if (
      this._account.name === 'Name' ||
      this._account.name === '' ||
      this._account.name === this._account.account
    ) {
      this._account.name =
        ACCOUNT_NAMES[appConfig.whiteLabelPartner][
          this._account.account_type_info.account_type
        ];
    }

    if (this._account.account_type_info.is_trading) {
      BonusAPI.getBonusList(this._account.account_id).then((data) => {
        this.accountBonus = data;
      });
    }
  }

  getOrderMessage = {
    type: TRADING_TYPES.GET_ORDERS,
    callback: (message) => this._handleOrders(message),
    pollingInterval: 1000,
  };

  getAccountMessage = {
    type: TRADING_TYPES.GET_ACCOUNT,
    callback: (message) => this._handleAccount(message),
    throttleInterval: config.maxIntervalForGetAccountMs,
    pollingInterval: 1000,
  };

  normalizeTimeToTimeFrame = (time) => {
    const timeFrameSecond = this.timeFrame * MILLISECONDS_PER_SECOND * 60;
    return Math.floor(time / timeFrameSecond) * timeFrameSecond;
  };

  get bonusInfo() {
    return (
      this.accountBonus?.find((bonus) => bonus?.info?.status === 'activated') ||
      null
    );
  }

  get hasRateToAccountCurrency() {
    return this.selectedSymbolPrice?.rate_to_account_currency;
  }

  get timeFrameInMs() {
    return this.timeFrame * 60 * MILLISECONDS_PER_SECOND;
  }

  get symbolSubscribeMessages() {
    return Object.entries(this.symbolsSubscribers)
      .filter(([, val]) => val)
      .map(([, val]) => val.msg);
  }

  get isArchived() {
    return this._account?.archived;
  }

  get isMaster() {
    return this._account?.account_type === ACCOUNT_TYPES.masterReal;
  }

  get isInvestor() {
    return this._account?.account_type === ACCOUNT_TYPES.investorReal;
  }

  get isDemo() {
    return this._account?.account_type === ACCOUNT_TYPES.forexDemo;
  }

  get isTradingAcc() {
    return this._account?.role === 'default' || this._account?.role === 'demo';
  }

  get isCommission() {
    return this._account?.account_type === ACCOUNT_TYPES.commissionReal;
  }

  get commission() {
    return this.symbol?.commission_in + this.symbol?.commission_out;
  }

  get digits() {
    return this.symbol?.digits;
  }

  get volumeStep() {
    return this.symbol?.volume_step;
  }

  get kRate() {
    return (
      this.selectedSymbolPrice &&
      this.selectedSymbolPrice.rate_to_account_currency
    );
  }

  get stopsLevel() {
    return this.symbol.stops_level;
  }

  get contractSize() {
    return this.symbol.contract_size;
  }

  get socketProvider() {
    return this.serverInfo.provider;
  }

  setOpenTrade(isOpen) {
    this.isOpenTrade = isOpen;
    if (this.isOpenTrade) {
      LocalStorage.set('last_opened_account', this.accountNumber);
    }
  }

  setChartRef(ref) {
    this.chartRef = ref;
  }

  scrollToChartRef() {
    if (this.chartRef) {
      this.chartRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }

  initStreaming() {
    if (!this.isStreamInitiated) {
      this.isStreamInitiated = true;
      this.initTradingSocket();
      this.initPricingSocket();

      this.afterSteaming && this.afterSteaming();
    }
  }

  endStreaming() {
    if (this.isStreamInitiated) {
      this.tradingSocket.destroy();
      this.pricingSocket.destroy();
      this.selectedSymbolName = null;
      this.symbolsSubscribers = {};
      this.lastTick = {};
      this.isStreamInitiated = false;
    }
  }

  async _getPSPList() {
    try {
      const data = await AccountService.getPSPList({
        id: this.accountNumber,
        type: this._account.account_type,
      });
      runInAction(() => {
        this.depositList = [];
        this.withdrawList = [];
        if (!data) {
          return;
        }
        for (let i = 0; i < data.length; i++) {
          if (data[i].is_deposit) {
            this.depositList.push(data[i]);
          }
          if (data[i].is_withdraw) {
            this.withdrawList.push(data[i]);
          }
        }
      });
    } catch (error) {
      captureError(error);
    }
  }

  updateName = async (name) => {
    trackPageEvent(`${this.accountTypeName}-change_name`);
    try {
      await this.updateAccount({ name });
      Notifier.success(i18n.t('common.notification.update_name.success'));
    } catch (err) {
      Notifier.error(i18n.t('common.notification.update_name.error'));
    }
  };

  async updateLeverage(leverage) {
    if (IS_ROCKWEST)
      trackAnalyticEvent(
        `trading_account_${this.accountTypeName}-change_leverage`
      );
    try {
      await this.updateAccount({ leverage: String(leverage) });
      this._account.leverage = leverage;
      Notifier.success(i18n.t('common.notification.update_leverage.success'));
    } catch (err) {
      Notifier.error(i18n.t('common.notification.update_leverage.error'));
    }
  }

  async unzip() {
    try {
      await this.updateAccount({ archive: false });
      Notifier.success(i18n.t('common.notification.unzip.success'));
    } catch (err) {
      Notifier.error(i18n.t('common.notification.unzip.error'));
    }
  }

  async updateTradingPassword(tradingPassword) {
    try {
      await this.updateAccount({ trading_pass: tradingPassword });
      Notifier.success(
        i18n.t('common.notification.update_trading_password.success')
      );
    } catch (err) {
      Notifier.error(
        i18n.t('common.notification.update_trading_password.error')
      );
    }
  }

  async updateInvestPassword(investPassword) {
    try {
      await this.updateAccount({ invest_pass: investPassword });
      Notifier.success(
        i18n.t('common.notification.update_invest_password.success')
      );
    } catch (error) {
      captureError(error);
      Notifier.error(
        i18n.t('common.notification.update_invest_password.error')
      );
    }
  }

  async updateAccount(data) {
    return AccountService.updateAccount(this.accountID, data);
  }

  initTradingSocket() {
    const _this = this;
    const streamURL = getStreamUrl(
      TRADING_STREAM_URL,
      AuthStore.authToken,
      this.socketProvider,
      this._account.account
    );

    this.tradingSocket = new WSClient(streamURL, {
      onStatusChanged(status) {
        _this.setTradingSocketStatus(status);
      },
      onDeleteSubscriptionError(msg) {
        if (msg) {
          captureError(
            new SocketError(
              `Cannot find msg ${JSON.stringify(msg)} to delete sub`
            )
          );
        } else {
          captureError(new SocketError('msg is undefined'));
        }
      },
      name: 'Trading WebSocket',
      maxFailures: appConfig.wsRetries,
      maxReconnects: appConfig.wsFailsMax,
      silenceTimeoutSeconds: appConfig.wsSilenceTimeout,
      connectingTimeoutSeconds: appConfig.wsConnectingTimeout,
    });

    this.tradingSocket.onSubscribe(this.getAccountMessage);

    this.tradingSocket.onSubscribe(this.getOrderMessage);

    this.tradingSocket.onSubscribe({
      type: PRICING_TYPES.GET_SERVER_TIME,
      callback: (message) => this.handleServerTime(message),
      handleType: 'Time',
      isRepeat: false,
      onSend: () => {
        this.requestedTime = +new Date();
      },
    });
    this.tradingSocket.onSubscribe({
      type: PRICING_TYPES.GET_INSTRUMENTS,
      callback: (message) => this.handleInstruments(message),
      isRepeat: false,
    });

    this.tradingSocket.init();
  }

  initPricingSocket() {
    const streamURL = getStreamUrl(
      PRICING_STREAM_URL,
      AuthStore.authToken,
      this.socketProvider,
      this._account.account
    );
    this.pricingSocket = new WSClient(streamURL, {
      name: 'Pricing WebSocket',
      onDeleteSubscriptionError(msg) {
        if (msg) {
          captureError(
            new SocketError(
              `Cannot find msg ${JSON.stringify(msg)} to delete sub`
            )
          );
        } else {
          captureError(new SocketError('msg is undefined'));
        }
      },
      maxFailures: appConfig.wsRetries,
      maxReconnects: appConfig.wsFailsMax,
      silenceTimeoutSeconds: appConfig.wsSilenceTimeout,
      connectingTimeoutSeconds: appConfig.wsConnectingTimeout,
    });

    this.pricingSocket.onSubscribe({
      type: PRICING_TYPES.GET_SERVER_TIME,
      callback: () => {},
      handleType: 'Time',
      isRepeat: true,
    });
    this.pricingSocket.init(() => {
      runInAction(() => {
        this.isPricingSocketInitiated = true;
      });
    });
  }

  setTradingSocketStatus = (status) => {
    this.tradingSocketStatus = status;
  };

  fetchAccountOperations(params) {
    return AccountService.getAccountOperations(this.accountID, params).then(
      async (data) => {
        const result = data.data;
        const positions = data.data.map((position) =>
          AccountService.getPosition(
            this.accountID,
            position.operation.position_id
          ).then((posData) => {
            posData.data.forEach((pos) => {
              if (
                !result.some(
                  (savedPosition) =>
                    savedPosition.operation.order_id === pos.operation.order_id
                )
              ) {
                result.push(pos);
              }
            });
          })
        );
        await Promise.allSettled(positions);
        result.forEach((position) => {
          const foundSamePositions = result.filter(
            (el) =>
              el.operation.position_id === position.operation.position_id &&
              el.operation.order_id !== position.operation.order_id
          );
          const all = [...foundSamePositions, position].sort(
            (a, b) => a.time - b.time
          );
          const [main] = all.splice(0, 1);
          if (!main.related) main.related = [];
          all.forEach((foundPos) => {
            const foundIdx = result.findIndex(
              (el) => el.trade_id === foundPos.trade_id
            );
            result.splice(foundIdx, 1);
            main.related.push(foundPos);
            main.profit += foundPos.profit;
          });
        });
        this.operations = result.sort(sortByTimeField(false));
        return data;
      }
    );
  }

  changeChartType(chartType) {
    LocalStorage.set(`chartType:${this.accountNumber}`, chartType);
    this.chartType = chartType;
  }

  setTimeFrame(timeFrame) {
    LocalStorage.set(`timeFrame:${this.accountNumber}`, timeFrame);
    this.timeFrame = timeFrame;
    this.boundaryCandleRequested = false;
    this.loadCandles({ isClearCandles: true });
    this.loadQuotes({ isClearQuotes: true });
  }

  setSelectedSymbolName(symbolName) {
    let setSymbolName = null;
    const isCorrectSymbolName = this.instruments.some(
      (instrument) => instrument.symbol === symbolName
    );
    if (isCorrectSymbolName) {
      setSymbolName = symbolName;
    }
    if (
      !isCorrectSymbolName &&
      first(this.instruments) &&
      first(this.instruments).symbol
    ) {
      setSymbolName = first(this.instruments).symbol;
    }

    if (!setSymbolName) return;

    this.subscribeForSelectedSymbol(setSymbolName);
    this.selectedSymbolName = setSymbolName;
    this.boundaryCandleRequested = false;
    this.loadCandles({ isClearCandles: true });
    this.loadQuotes({ isClearQuotes: true });
    LocalStorage.set(`selectedSymbolName:${this.accountNumber}`, setSymbolName);
  }

  _handleAccount(message) {
    this._account.equity = message.equity;
    this._account.margin = message.margin;
    this._account.balance = message.balance;
    this._account.free_margin = message.free_margin;
  }

  loadQuotes({ dateTo = new Date(), isClearQuotes } = {}) {
    if (isClearQuotes) {
      this.quotesResponse = [];
    }
    const to = dateTo.getTime();
    const from = to - 60 * MILLISECONDS_PER_SECOND;

    PricingService.getQuotes(this.selectedSymbolName, {
      from,
      to,
    })
      .then((data) => {
        if (!Array.isArray(data)) {
          return;
        }
        const firstQuote = first(this.quotesResponse) || {};
        const lastQuote = last(data) || {};
        if (!firstQuote.time && !lastQuote.time) {
          return;
        }
        if (firstQuote.time && firstQuote.time < lastQuote.time) {
          return;
        }
        this.quotesResponse = [...data, ...this.quotesResponse];
      })
      .catch((error) => {
        captureError(error);
      });
  }

  setSelectedCategory(category) {
    this.selectedCategory = category;
  }

  loadCandles({ dateTo, dateFrom, isClearCandles = false } = {}) {
    if (isClearCandles) {
      this.candlesResponse = [];
    }
    let nextFetchesCount = 0;
    let to = +dateTo;
    if (+dateTo > +new Date()) to = null;
    const toDefault = +to || +new Date();
    const from = dateFrom
      ? +dateFrom
      : toDefault - this.timeFrameInMs * CANDLE_COUNT;
    const timeIntervalDelta = toDefault - from;

    if (!to) to = toDefault;

    const symbol = this.selectedSymbolName;

    const handlingCandles = (data) => {
      if (symbol !== this.selectedSymbolName) {
        return;
      }
      if (!Array.isArray(data)) {
        captureError(
          new InfrastructureError('Candles endpoint answered empty object')
        );
        return;
      }
      data = data
        .sort((a, b) => a.time - b.time)
        .map((el) => ({ ...el, isHistorical: true }));
      const firstStoredCandle = first(this.candlesResponse) || {};
      const firstCandle = first(data) || firstStoredCandle || {};
      const lastCandle = last(data) || {};
      if (!firstStoredCandle.time && !lastCandle.time) {
        captureError(
          new InfrastructureError(
            'Candles endpoint response contained candles without time'
          )
        );
        return;
      }
      if (firstStoredCandle.time && firstStoredCandle.time < lastCandle.time) {
        log('first candle time error!', 'error');
        return;
      }

      this.candlesResponse = [...data, ...this.candlesResponse];
      this.candlesResponse.sort((a, b) => a.time - b.time);
      if (
        firstCandle.time - from >
          this.timeFrame * MILLISECONDS_PER_SECOND * SECONDS_PER_MINUTE * 2 &&
        nextFetchesCount <= appConfig.maxChartHistoryFetchesCount
      ) {
        nextFetchesCount++;
        PricingService.getCandles(
          this.selectedSymbolName,
          getIntervalByValue(this.timeFrame).key,
          {
            from: from - timeIntervalDelta * nextFetchesCount,
            to: to - timeIntervalDelta * nextFetchesCount,
          }
        )
          .then(handlingCandles)
          .catch((error) => {
            captureError(error);
          });
      }
    };

    PricingService.getCandles(
      this.selectedSymbolName,
      getIntervalByValue(this.timeFrame).key,
      {
        from,
        to,
      }
    )
      .then((resp) => {
        runInAction(() => {
          handlingCandles(resp);
        });
      })
      .catch((error) => {
        captureError(error);
      });
  }

  fetchBoundaryCandlePromise = null;

  fetchBoundaryCandle = (from) => {
    const MAX_FETCHES = 7;
    let fetchCounter = 0;

    if (this.fetchBoundaryCandlePromise) {
      this.fetchBoundaryCandlePromise.reject?.();
    }

    this.boundaryCandleRequested = true;

    const handleReceivingBoundaryCandle = (data) => {
      if (data.length === 0) {
        fetchCounter++;
        if (this.timeFrame === 1) {
          setTimeout(() => {
            if (this.boundaryCandleRequested) {
              PricingService.getCandles(
                this.selectedSymbolName,
                getIntervalByValue(this.timeFrame).key,
                {
                  from,
                  to: from + this.timeFrame * 60 * MILLISECONDS_PER_SECOND,
                }
              ).then(handleReceivingBoundaryCandle);
            }
          }, 1000 * (3 * fetchCounter));
        }
      } else if (fetchCounter >= MAX_FETCHES) {
        log(`Error fetching boundary tick ${from}, its empty`, 'error');
      } else {
        runInAction(() => {
          data = { ...data[0], isHistorical: true, isReplaced: true };
          const foundAlreadyPresentedTickIdx = this.candlesResponse.findIndex(
            (el) => el.time === data.time
          );
          if (foundAlreadyPresentedTickIdx >= 0) {
            this.candlesResponse[foundAlreadyPresentedTickIdx] = data;
          } else {
            this.candlesResponse = [data, ...this.candlesResponse];
            this.candlesResponse.sort((a, b) => a.time - b.time);
          }
        });
      }
    };

    this.fetchBoundaryCandlePromise = PricingService.getCandles(
      this.selectedSymbolName,
      getIntervalByValue(this.timeFrame).key,
      {
        from,
        to: from + this.timeFrame * 59 * MILLISECONDS_PER_SECOND,
      }
    ).then(handleReceivingBoundaryCandle);
  };

  handleLastPrices(response) {
    response.forEach((tick) => {
      this.lastTick[tick.symbol] = tick;
    });
  }

  handleTickReceive(tick) {
    if (!tick?.symbol || !tick?.ask || !tick?.bid || !tick?.time) return;

    const digits = this.precision[tick.symbol] || 5;
    const modifiedTick = {
      ...tick,
      time: tick.time - this.shiftTime,
      ask: parseFloat(tick.ask.toFixed(digits)),
      bid: parseFloat(tick.bid.toFixed(digits)),
    };

    this.lastTick[modifiedTick.symbol] = modifiedTick;

    if (modifiedTick.symbol === this.selectedSymbolName) {
      const time = this.normalizeTimeToTimeFrame(modifiedTick.time);
      const lastCandle = this.candlesResponse[this.candlesResponse.length - 1];

      if (modifiedTick.time && modifiedTick.bid) {
        this.quotesResponse.push({
          bid: modifiedTick.bid,
          ask: modifiedTick.ask,
          time: modifiedTick.time,
        });
      }

      if (lastCandle) {
        const lastCandleTimeNormalized = this.normalizeTimeToTimeFrame(
          lastCandle.time
        );
        const timeCondition = time > lastCandleTimeNormalized;
        if (timeCondition) {
          const value = lastCandle.close;
          const high = value > modifiedTick.bid ? value : modifiedTick.bid;
          const low = value < modifiedTick.bid ? value : modifiedTick.bid;

          const firstStreamedCandle = {
            open: value,
            close: modifiedTick.bid,
            low,
            high,
            bid: modifiedTick.bid,
            ask: modifiedTick.ask,
            time,
            date: new Date(time),
          };
          this.candlesResponse.push(firstStreamedCandle);
          if (
            !this.candlesResponse[this.candlesResponse.length - 2]?.isReplaced
          ) {
            this.fetchBoundaryCandle(time - this.timeFrameInMs);
          }
        } else {
          lastCandle.bid = modifiedTick.bid;
          lastCandle.ask = modifiedTick.ask;
          lastCandle.close = modifiedTick.bid;
          if (lastCandle.high < modifiedTick.bid) {
            lastCandle.high = modifiedTick.bid;
          }
          if (lastCandle.low > modifiedTick.bid) {
            lastCandle.low = modifiedTick.bid;
          }
        }
      }
    }

    const isLastHistoricalCandleDropped =
      this.normalizeTimeToTimeFrame(this.lastHistoricalCandle?.time) +
        2 * this.timeFrameInMs ===
      this.firstStreamedCandle?.time;

    if (isLastHistoricalCandleDropped && !this.boundaryCandleRequested) {
      this.fetchBoundaryCandle(
        this.normalizeTimeToTimeFrame(this.lastHistoricalCandle?.time) +
          this.timeFrameInMs
      );
    }
  }

  get firstStreamedCandle() {
    return first(this.candlesResponse.slice().filter((el) => !el.isHistorical));
  }

  get lastHistoricalCandle() {
    return last(this.candlesResponse.slice().filter((el) => el.isHistorical));
  }

  subscribeForSelectedSymbol = (symbolName) => {
    const msg = {
      type: PRICING_TYPES.TICKS_SUBSCRIBE,
      body: { symbol: symbolName },
      callback: (tick) => this.handleTickReceive(tick),
      handleType: 'Tick',
      isRepeat: false,
      nonDelete: true,
    };

    const symbolsSubscriber = this.symbolsSubscribers[symbolName];
    if (symbolsSubscriber === undefined) {
      this.symbolsSubscribers[symbolName] = { msg };

      const onSubscribe = () => {
        this.tradingSocket.onSubscribe(msg);
      };

      if (this.tradingSocket) {
        onSubscribe();
      } else {
        this.afterSteaming = onSubscribe;
      }
    }
  };

  _handleOrders(message) {
    if (message.orders) {
      syncObservableArray(
        this.openOrders,
        message.orders,
        (item) => item.ticket
      );
      this.openOrders.forEach((o) => {
        o.profit = Math.round(o.profit * 100) / 100;
      });
    }
    if (message.pendings) {
      syncObservableArray(
        this.pendingOrders,
        message.pendings,
        (item) => item.ticket
      );
      this.pendingOrders.forEach((o) => {
        o.profit = Math.round(o.profit * 100) / 100;
      });
    }
  }

  handleServerTime(message) {
    if (!message) return;
    if (message.code) {
      captureError(new BackendError(message));
      return;
    }
    this.shiftTime = message.time - this.requestedTime;
    this.serverTime = message.time;
  }

  handleInstruments(message) {
    if (!message) {
      return;
    }
    if (message.code) {
      captureError(new BackendError(message));
      return;
    }
    this.instruments = [];
    this.precision = {};
    message.forEach((value) => {
      const oldValue = value.symbol;
      let newValueStr = '';
      let fancySymbol = '';
      let foundCoin = {};
      const categories = [];

      Object.keys(TICKERS_CATEGORIES).forEach((key) => {
        if (
          TICKERS_CATEGORIES[key].symbols.find((symbol) =>
            oldValue.includes(symbol)
          )
        ) {
          categories.push(key);
        }
      });

      if (oldValue.includes('.')) {
        const oldValueIndex = oldValue.indexOf('.');
        newValueStr = oldValue.slice(0, oldValueIndex);
      } else {
        newValueStr = oldValue;
      }

      const codesList = codes().concat(CRYPTO_TICKERS);
      const code = codesList.find((currency) => newValueStr.endsWith(currency));

      if (code) {
        const newValueIndex = newValueStr.indexOf(code);
        const newValueSubstring = newValueStr.slice(0, newValueIndex);

        fancySymbol = `${newValueSubstring} / ${code}`;
      } else {
        fancySymbol = newValueStr;
      }

      if (!IS_DISONA) {
        foundCoin = DEFAULT_CURRENCY_ICONS.find(({ label }) =>
          newValueStr.includes(label)
        );
      } else {
        foundCoin = DISONA_CURRENCY_ICONS.find(({ label }) =>
          newValueStr.includes(label)
        );
      }
      value = {
        ...value,
        fancySymbol,
        categories,
        ...foundCoin,
      };

      if (value.trade_mode === 4) {
        this.instruments.push(value);
        this.precision[value.symbol] = value.digits;
      }
    });
    LocalStorage.set(`instruments:${this.accountNumber}`, this.instruments);
    if (!this.selectedSymbolName) {
      const symbolName =
        LocalStorage.get(`selectedSymbolName:${this.accountNumber}`) ||
        `EURUSD${this.accountTypeName === 'mini' ? 'm' : ''}`;

      this.setSelectedSymbolName(symbolName);
    }
  }

  setActiveTabView = (view) => {
    trackAnalyticEvent(`trading_account_${this.accountTypeName}-go_to_${view}`);
    this.activeTabView = view;
  };

  isShowFullOrderModal = false;

  setIsShowFullOrderModal(val) {
    this.isShowFullOrderModal = val;
  }

  getInstrumentBySymbol = (symbol) =>
    this.instruments.find((instrument) => instrument.symbol === symbol);

  get id() {
    return this._account?.account_id;
  }

  get quotes() {
    return this.quotesResponse.map((quote) => ({
      ...quote,
      close: quote.bid,
      date: new Date(quote.time),
    }));
  }

  get getInstruments() {
    return this.instruments;
  }

  get candles() {
    return this.candlesResponse.slice().map((candle) => ({
      ...candle,
      bid: candle.close,
      ask: candle.close,
      date: new Date(candle.time),
    }));
  }

  get selectedSymbolPrice() {
    const result = this.lastTick[this.selectedSymbolName];
    if (!result) return null;
    return 'ask' in result ? result : null;
  }

  get accountNumber() {
    return this.accountInfo.account;
  }

  get accountID() {
    return this.accountInfo.account_id;
  }

  get accountInfo() {
    return this._account || {};
  }

  get serverInfo() {
    return this.accountInfo.server_info || {};
  }

  get accountTypeName() {
    return this.accountInfo.account_type_info.account_type.includes('demo')
      ? 'demo'
      : 'real';
  }

  get accountName() {
    return this.accountInfo.account_type_info.account_type;
  }

  get accountCurrency() {
    return this.accountInfo.account_type_info.currency || '';
  }

  get isTrading() {
    return this.accountInfo.account_type_info.is_trading;
  }

  get profit() {
    return (
      Math.round((this.accountInfo.equity - this.accountInfo.balance) * 100) /
      100
    );
  }

  get symbol() {
    return this.getInstrumentBySymbol(this.selectedSymbolName);
  }

  get selectedFancySymbolName() {
    return this.symbol?.fancySymbol;
  }

  get symoblQouteCurrency() {
    return this.symbol.quote_currency;
  }

  get leverages() {
    const leverages =
      this.isMaster || this.isInvestor ? [100] : [50, 100, 200, 400, 500];
    return leverages
      .sort((a, b) => a - b)
      .map((value) => {
        const label = value > 2000000 ? 'Infinity' : `1:${value}`;
        return {
          value,
          label,
        };
      });
  }

  get hasOpenedOrders() {
    return this.openOrders.length > 0;
  }

  get preparedOpenOrders() {
    return this.openOrders.map((order, i) =>
      prepareOrder(order, this.lastTick, this.getInstrumentBySymbol, i + 1)
    );
  }

  get preparedOpenOrdersForSymbol() {
    return this.preparedOpenOrders.filter(
      (el) => el.symbol === this.selectedSymbolName
    );
  }

  get preparedPendingOrders() {
    return this.pendingOrders.map((order) =>
      prepareOrder(order, this.lastTick, this.getInstrumentBySymbol)
    );
  }

  get isTradingSocketConnected() {
    return this.tradingSocketStatus === SocketConnectionStatuses.Connected;
  }

  getPriceForOpenOrder(type) {
    let price;
    const symbolPrice = this.selectedSymbolPrice;
    if (type === OrderTypes.Buy) {
      price = symbolPrice.ask;
    } else if (type === OrderTypes.Sell) {
      price = symbolPrice.bid;
    }
    return price;
  }

  OpenOrder = (order) => {
    this.isOpeningOrder = true;
    const msg = {
      type: TRADING_TYPES.OPEN_ORDER,
      body: order,
      callback: (message) => {
        this.isOpeningOrder = false;
        if (isErrorMessage(message)) {
          Notifier.error(i18n.t('common.notification.open_order.error'));
        } else {
          Notifier.success(i18n.t('common.notification.open_order.success'));
          const isOpen =
            order.type === OrderTypes.Buy || order.type === OrderTypes.Sell;
          this.setActiveTabView(isOpen ? 'open' : 'pending');
        }
        this.tradingSocket.send(this.getOrderMessage);
      },
    };
    this.tradingSocket.send(msg);
  };

  UpdateOrder(order, onCallback) {
    const msg = {
      type: TRADING_TYPES.UPDATE_ORDER,
      body: order,
      callback: (message) => {
        onCallback?.(message);
        this.tradingSocket.send(this.getOrderMessage);
      },
    };
    this.tradingSocket.send(msg);
  }

  ModifyOrder(order) {
    const msg = {
      type: TRADING_TYPES.MODIFY_ORDER,
      body: order,
    };
    this.tradingSocket.send(msg);
  }

  CloseOrder(order) {
    const body = { ...closeOrder };
    body.ticket = order.ticket;
    body.volume = order.volume;
    this.tradingSocket.send({
      type: TRADING_TYPES.CLOSE_ORDER,
      body,
      callback: (message) => {
        if (isErrorMessage(message)) {
          Notifier.error(i18n.t('common.notification.close_order.error'));
        } else if (Notifier.config.notify.manualDeals) {
          Notifier.success(i18n.t('common.notification.close_order.success'));
        }
        this.tradingSocket.send(this.getOrderMessage);
      },
    });
  }

  MakeDeposit(guid, amount) {
    return AccountService.makeDeposit({
      amount,
      pspguid: guid,
      account: this._account,
      payload: {},
    });
  }

  MakeWithdrawal(guid, amount) {
    const data = {
      amount,
      pspguid: guid,
      account: this._account,
      payload: {},
    };

    return AccountService.makeWithdrawal(data);
  }
}
